/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useStore, useStoreMap } from 'effector-react';
import { useMutation, useQuery } from 'react-query';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import { toFlag } from 'pano-utils';
import { getUnixTime, format, fromUnixTime } from 'date-fns';
import { API_DOMAIN, HOST, VIP_URL } from '@constants';
import { type BooleanFlag, PrivilegeType } from '@types';
import { $isTeamAccount, $isVip } from '@stores/memberStores';
import Button from '@components/Button';
import TextLink from '@components/TextLink';
import axios from '@utils/axios';
import { $tourData } from '@stores/dataStores';
import { sendSuccess } from '@components/Noti';
import useToggle from '@hooks/useToggle';
import { DatePicker } from '@components/DatePicker';
import { teamConfig, useTeamConfig } from '@utils/teamConfig';
import type { ShareSetting } from './types';

type TempLink = {
  id: number;
  hashId: string;
  ttl: number;
  createDate: number;
  expireDate: number;
  showExpireDate: BooleanFlag;
  operatorId?: number;
  operatorName?: string;
};

// 30分钟
const MIN_TTL = 30 * 60;
// 7天
const MAX_TTL = 7 * 24 * 60 * 60;

const Col = styled.div`
  display: flex;
  height: 38px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-right: 1px solid #efefef;
`;
const ItemCol = styled(Col)`
  height: 52px;
  word-break: break-all;
  text-align: center;
  font-size: 12px;
`;

type Props = {
  onPickerToggle: (isOn: boolean) => void;
  settings: ShareSetting;
};

export function ShareTempLinks({ settings, onPickerToggle }: Props) {
  const isVip = useStore($isVip);
  const isTeamAccount = useStore($isTeamAccount);
  const [date, setDate] = useState<Date | null>(null);
  const showExpireTime = useToggle(false);
  const tourId = useStoreMap({
    store: $tourData,
    fn: (data) => data.id,
    keys: [],
  });
  const listUrlPart = useTeamConfig('/member/my/tour', '/team/app/tours');

  const { data: linkList = [], refetch } = useQuery(
    ['tempLinks', tourId, listUrlPart],
    () => {
      return axios.get<TempLink[]>(
        `${API_DOMAIN}${listUrlPart}/${tourId}/temps`
      );
    }
  );

  const createLink = useMutation(
    async () => {
      if (!date || !tourId) return;
      const ttl = getUnixTime(date) - getUnixTime(new Date());
      if (ttl < MIN_TTL) {
        throw new Error('链接有效时长不少于30分钟，请重新选择');
      }
      if (ttl > MAX_TTL) {
        throw new Error('链接有效时长不超过7天，请重新选择');
      }
      const createUrl = teamConfig(
        `${API_DOMAIN}/member/my/tour/temp`,
        `${API_DOMAIN}/team/app/tours/temp`
      );
      return axios.post<{ hashId: string }>(createUrl, {
        tourId,
        ttl,
        showExpireDate: toFlag(showExpireTime.isOn),
      });
    },
    {
      onSuccess: () => {
        sendSuccess('临时分享已生成');
        refetch();
      },
    }
  );

  const deleteLink = useMutation(
    (id: number) => {
      const delUrl = teamConfig(
        `${API_DOMAIN}/member/my/tour/temp/${id}`,
        `${API_DOMAIN}/team/app/tours/temp/${id}`
      );
      return axios.delete(delUrl);
    },
    {
      onSuccess: refetch,
    }
  );

  const renderHeader = () => {
    return (
      <div className="flex flex-col bg-white-light p-20">
        <div className="flex items-center">
          <div className="mr-5">链接有效期：</div>
          <div className="w-160">
            <DatePicker
              className="h-30 border px-10"
              selected={date}
              onChange={(val) => {
                setDate(val);
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="time"
              dateFormat="yyyy-MM-dd HH:mm"
              onCalendarOpen={() => onPickerToggle(true)}
              onCalendarClose={() => onPickerToggle(false)}
            />
          </div>
          <label className="ml-30 flex cursor-pointer select-none items-center">
            <input
              checked={showExpireTime.isOn}
              type="checkbox"
              onChange={() => showExpireTime.toggle()}
            />
            <span className="ml-5">在作品中显示失效时间</span>
          </label>
          <Button
            disabled={
              !isVip || !date || createLink.isLoading || linkList.length >= 20
            }
            className="ml-auto"
            onClick={() => {
              createLink.mutate();
            }}
          >
            生成临时分享
          </Button>
        </div>
        <div className="mt-20 text-orange">
          注：最多可生成20个链接，有效期不超过7天。链接删除后作品自动失效。
        </div>
      </div>
    );
  };

  const renderDateCol = (timestamp: number) => {
    const colDate = fromUnixTime(timestamp);
    return (
      <ItemCol style={{ width: isTeamAccount ? 90 : 120 }}>
        <div>{format(colDate, 'yyyy-MM-dd')}</div>
        <div>{format(colDate, 'HH:mm:ss')}</div>
      </ItemCol>
    );
  };

  const renderItem = (item: TempLink) => {
    const src = `${HOST}/expired/vr/${item.hashId}`;
    return (
      <div key={item.id} className="flex items-center border-b">
        <ItemCol
          style={{ width: isTeamAccount ? 160 : 200, padding: '0 10px' }}
        >
          {src}
        </ItemCol>
        {!!isTeamAccount && (
          <ItemCol style={{ width: 110, wordBreak: 'unset' }} className="px-10">
            <div className="w-full truncate" title={item.operatorName}>
              {item.operatorName ?? ''}
            </div>
          </ItemCol>
        )}
        {renderDateCol(item.createDate)}
        {renderDateCol(item.expireDate)}
        <ItemCol className="grow">{item.showExpireDate ? '是' : '否'}</ItemCol>
        <ItemCol style={{ width: 150, padding: '0 20px' }}>
          <div className="flex w-full items-center justify-between">
            <TextLink
              className="text-sm"
              onClick={() => {
                const text =
                  settings.privilege === PrivilegeType.password
                    ? `${src}\n访问密码: ${settings.password ?? ''}`
                    : src;
                navigator.clipboard?.writeText(text).then(() => {
                  sendSuccess('链接地址已复制到剪贴板');
                });
              }}
            >
              复制
            </TextLink>
            <TextLink href={src} target="_blank" className="text-sm">
              查看
            </TextLink>
            <TextLink
              className="text-sm"
              onClick={() => {
                deleteLink.mutate(item.id);
              }}
            >
              删除
            </TextLink>
          </div>
        </ItemCol>
      </div>
    );
  };

  const renderBody = () => {
    if (isTeamAccount) {
      return (
        <div className="flex h-full flex-col border-t">
          <div className="flex items-center border-b bg-white-light">
            <Col style={{ width: 160 }}>链接地址</Col>
            <Col style={{ width: 110 }}>创建人</Col>
            <Col style={{ width: 90 }}>生成时间</Col>
            <Col style={{ width: 90 }}>失效时间</Col>
            <Col className="grow">显示失效时间</Col>
            <Col style={{ width: 150 }}>操作</Col>
          </div>
          <div className="flex-1 pb-20">
            <Scrollbars>{linkList?.map(renderItem)}</Scrollbars>
          </div>
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col border-t">
        <div className="flex items-center border-b bg-white-light">
          <Col style={{ width: 200 }}>链接地址</Col>
          <Col style={{ width: 120 }}>生成时间</Col>
          <Col style={{ width: 120 }}>失效时间</Col>
          <Col className="grow">显示失效时间</Col>
          <Col style={{ width: 150 }}>操作</Col>
        </div>
        <div className="flex-1 pb-20">
          <Scrollbars>{linkList?.map(renderItem)}</Scrollbars>
        </div>
      </div>
    );
  };

  const renderVipInfo = () => {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="text-lg">升级个人专业版/企业版后即可使用</div>
        <div className="mt-10 text-center text-sm" style={{ maxWidth: 500 }}>
          该功能允许用户在向甲方正式交付项目前，生成有时间限制的临时分享给到对方预览，到期后链接自动失效，大大减小了跑单的风险。
        </div>
        <TextLink href={VIP_URL} target="_blank" className="mt-10">
          去升级
        </TextLink>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {renderHeader()}
      {isVip && renderBody()}
      {!isVip && renderVipInfo()}
    </div>
  );
}
